import React from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faImage, faArrowUp, faArrowDown, faImageUser, faGrid } from '@fortawesome/pro-regular-svg-icons';
import { faQuoteRight } from '@fortawesome/pro-solid-svg-icons';
import { confirmAlert } from 'react-confirm-alert';
import ConfirmPrompt from '../General/ConfirmPrompt';
import './ShopTile.scss';

import { getFeaturedResultType, getShopFeaturedResultsForType, getFeatureDisplayData } from '../../Helpers/featured_helpers';
import { dontBubble } from '../../Helpers/helpers';
import { getGMTTime } from '../../Helpers/formatting';
import { updateShopFeature, deleteShopFeature } from '../../APIClient/featured';

import ImageUploader from '../General/ImageUploader';
import Tooltip from '../General/Tooltip';

const ShopTile = props => {
  const { feature, featured } = props;

  // UI Data
  const featureDisplayData = getFeatureDisplayData(feature);
  const { title, link } = featureDisplayData;

  // Deleting
  const deleteFeature = async () => {
    const confirm = window.confirm('Are you sure you want to delete this feature?');
    if (confirm) {
      await deleteShopFeature(feature);
      window.ALERT.success('Feature deleted');
      props.syncShopFeatures();
    }
  };

  // Sorting
  const moveFeature = async (direction = 'down') => {
    const featuresOfType = getShopFeaturedResultsForType(featured, getFeaturedResultType(feature));
    const currentIndex = _.findIndex(featuresOfType, f => f.id === feature.id);

    let newSortOrderRank;
    if (direction === 'down') {
      const nextFeature = featuresOfType[currentIndex + 1];
      const featureAfterNextFeature = featuresOfType[currentIndex + 2];

      if (!featureAfterNextFeature) newSortOrderRank = getGMTTime();
      else newSortOrderRank = (nextFeature.sortOrderRank + featureAfterNextFeature.sortOrderRank) / 2;
    }

    if (direction === 'up') {
      const previousFeature = featuresOfType[currentIndex - 1];
      const featureBeforePreviousFeature = featuresOfType[currentIndex - 2];

      if (!featureBeforePreviousFeature) newSortOrderRank = -1 * getGMTTime();
      else newSortOrderRank = (previousFeature.sortOrderRank + featureBeforePreviousFeature.sortOrderRank) / 2;
    }

    await updateShopFeature(feature, { sortOrderRank: newSortOrderRank });
    props.syncShopFeatures();
  };

  // Description
  const editDescription = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header='Add a message.'
          subheader='Message is about this.'
          placeholder='Add your message here.'
          onCancel={onClose}
          customInputFields={[
            {
              display: 'Description',
              placeholder: `Write a description for this ${featureDisplayData.singularType}.`,
              value: 'description',
              preloaded: feature.description
            }
          ]}
          onSubmitAwait={async responseValues => {
            await updateShopFeature(feature, { descriptionOverride: responseValues.description });
            props.syncShopFeatures();
            window.ALERT.success('Description updated!');
          }}
        />
      )
    });
  };

  // Sections
  const editSections = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmPrompt
          header={`Edit Sections`}
          onCancel={onClose}
          customInputFields={featured.shopSectionLabels.map(label => ({
            label,
            isBoolean: true,
            value: label,
            preloaded: true
          }))}
          onSubmitAwait={async responseValues => {
            const features = _.keys(_.omitBy(responseValues, value => !value)).join('|') || '';
            await updateShopFeature(feature, { sectionLabels: features });
            props.syncShopFeatures();
            window.ALERT.success('Sections updated!');
          }}
        />
      )
    });
  };

  // Image Management
  const [isEditingImage, setIsEditingImage] = React.useState(null);
  const [isEditingNonTextImage, setIsEditingNonTextImage] = React.useState(null);
  const showImageUploader = ({ nonTextImage = false } = {}) => {
    setIsEditingNonTextImage(nonTextImage);
    setIsEditingImage(!nonTextImage);
  };
  const hideImageUploader = () => {
    setIsEditingImage(null);
    setIsEditingNonTextImage(null);
  };
  const completeImageUploadFromModal = async url => {
    await updateShopFeature(feature, { [isEditingNonTextImage ? 'imageOverrideNonText' : 'imageOverride']: url });
    url ? window.ALERT.success('Image overridden') : window.ALERT.success('Image removed');
    props.syncShopFeatures();
  };

  const getFeatureAdminControls = feature => {
    return (
      window.__ADMIN_CONTROL_MODE__ && (
        <div onClick={dontBubble} className='admin-actions'>
          <Tooltip message={props.activeSection ? 'Please move down on the All tab' : 'Move Down'}>
            <div className={cn('action move-down', { disabled: props.activeSection })} onClick={() => moveFeature('down')}>
              <FontAwesomeIcon icon={faArrowDown} />
            </div>
          </Tooltip>
          <Tooltip message={props.activeSection ? 'Please move up on the All tab' : 'Move Up'}>
            <div className={cn('action move-up', { disabled: props.activeSection })} onClick={() => moveFeature('up')}>
              <FontAwesomeIcon icon={faArrowUp} />
            </div>
          </Tooltip>
          <Tooltip message='Edit Sections'>
            <div className='action edit-sections' onClick={editSections}>
              <FontAwesomeIcon icon={faGrid} />
            </div>
          </Tooltip>
          <Tooltip message='Edit Description'>
            <div className='action edit-description' onClick={editDescription}>
              <FontAwesomeIcon icon={faQuoteRight} />
            </div>
          </Tooltip>
          <Tooltip message='Edit Non-Text Image For Feature'>
            <div className='action edit-image' onClick={() => showImageUploader({ nonTextImage: true })}>
              <FontAwesomeIcon icon={faImageUser} />
            </div>
          </Tooltip>
          <Tooltip message='Edit Tile Image'>
            <div className='action edit-image' onClick={() => showImageUploader()}>
              <FontAwesomeIcon icon={faImage} />
            </div>
          </Tooltip>
          <Tooltip message='Delete Feature'>
            <div className='action delete-feature' onClick={deleteFeature}>
              <FontAwesomeIcon icon={faTimes} />
            </div>
          </Tooltip>
        </div>
      )
    );
  };

  // UI Image handling
  const [isImageLoaded, setIsImageLoaded] = React.useState(false);

  const containerRef = React.useRef(null);
  const additionalClasses = {
    'image-loaded': isImageLoaded
  };
  const onImageLoad = () => {
    // Fade in the image after a delay based on the position of the tile on the page for a shimmmer effect
    const centerPercentToLeft = (100 * (containerRef.current.offsetLeft + containerRef.current.offsetWidth / 2)) / window.innerWidth;
    const centerPercentFromTop = (100 * containerRef.current.offsetTop) / window.innerHeight;
    setTimeout(() => setIsImageLoaded(true), centerPercentToLeft + centerPercentFromTop * 1.5);
  };

  // Events
  const logClickToShop = () => {
    window.__ADD_EVENT__('Shop - Clicked Shop Tile', {
      source: props.eventSource || 'Unknown',
      title
    });
  };

  return (
    <>
      {(isEditingImage || isEditingNonTextImage) && (
        <ImageUploader
          isVisible
          initialImageUrl={isEditingNonTextImage ? feature.nonTextImage : feature.image}
          onSaveCallback={completeImageUploadFromModal}
          setIsVisible={hideImageUploader}
        />
      )}
      <div ref={containerRef} className={cn('shop-tile-outer-container', additionalClasses)}>
        <Link to={link} onClick={logClickToShop} className={cn('shop-tile-container', additionalClasses)}>
          {feature.image ? (
            <img className={cn('image', additionalClasses)} src={feature.image} alt={title} onLoad={onImageLoad} />
          ) : (
            <div className='image empty'>{title}</div>
          )}
          {getFeatureAdminControls(feature)}
        </Link>
      </div>
    </>
  );
};

ShopTile.propTypes = {
  featured: PropTypes.object.isRequired,
  feature: PropTypes.object.isRequired,
  activeSection: PropTypes.string, // If we are in a filtered section, we need to block reordering.
  syncShopFeatures: PropTypes.func.isRequired,
  fadeInDelay: PropTypes.number,
  eventSource: PropTypes.string
};

export default ShopTile;
